// Dessin du décor (voir core/scenery.js) sur le canvas du viseur : ciel, astre, étoiles, mer,
// plan lointain, oiseaux, premier plan et brume, avec les couleurs du décor du niveau
// (config/theme.js#sceneryPalettes). Tout est en silhouettes : chaque élément ajoute ses formes à
// trois tracés (main : la silhouette, accent : neige, bec…, glow : fenêtres et yeux qui brillent),
// remplis chacun d'une couleur. Quelques détails s'animent avec le temps (yeux qui clignent,
// ailes qui battent, liane qui se balance, brume qui dérive).
import { VIEW_SIZE } from '../config/constants.js';
import { view } from './viewState.js';
import { wrapX } from '../core/camera.js';
import { FAR_PARALLAX, milkyWayAxis, milkyWayTaper } from '../core/scenery.js';
import { TAU, outline, poly, disc, oval, limb, frame, eyesOpen, eyes } from './paintKit.js';
import { createGroundPainter } from './groundPainter.js';

// --- Formes partagées --------------------------------------------------------------------

function conifer(P, x, base, w, h) {
  const trunk = w * 0.16;
  P.m.rect(x + w / 2 - trunk / 2, base - h * 0.2, trunk, h * 0.2);
  // Trois étages de branches, du plus large (en bas) au plus pointu
  for (const [top, bottom, spread] of [[1, 0.55, 0.62], [0.8, 0.35, 0.82], [0.58, 0.15, 1]]) {
    const half = (w * spread) / 2;
    poly(P.m, [[x + w / 2, base - h * top], [x + w / 2 + half, base - h * bottom], [x + w / 2 - half, base - h * bottom]]);
  }
}

/** Cocotier : tronc arqué (pied en fx, fy), couronne de palmes et noix de coco. */
function palmTree(path, fx, fy, w, h, lean) {
  const tx = fx + lean * w * 0.35;
  const ty = fy - h * 0.78;
  const b = w * 0.07;
  const t = w * 0.035;
  const cx = fx + lean * w * 0.02;
  const cy = fy - h * 0.45;
  outline(path, [[fx - b, fy + 1], [tx - t, ty, cx - b, cy], [tx + t, ty], [fx + b, fy + 1, cx + b, cy]]);
  const thick = w * 0.07;
  for (const [dx, dy] of [[-1, 0.5], [-0.85, -0.05], [-0.4, -0.42], [0.08, -0.45], [0.5, -0.35], [0.92, -0.05], [1, 0.5]]) {
    const ex = tx + dx * w * 0.52;
    const ey = ty + dy * h * 0.5;
    const mx = (tx + ex) / 2;
    const my = (ty + ey) / 2 - Math.abs(dx) * h * 0.12;
    outline(path, [[tx, ty - thick * 0.3], [ex, ey, mx, my - thick], [tx, ty + thick * 0.3, mx, my + thick * 0.4]]);
  }
  disc(path, tx - w * 0.045, ty + w * 0.05, w * 0.045);
  disc(path, tx + w * 0.045, ty + w * 0.06, w * 0.045);
  disc(path, tx, ty + w * 0.1, w * 0.04);
}

/** Arbre mort aux branches crochues (et parfois un corbeau perché au bout d'une branche). */
function deadTree(P, x, base, w, h, p, k, t) {
  const at = frame(x, base, w, h, p);
  const tw = Math.max(1.5 * k, w * 0.12);
  const fork = at(0.46, 0.55);
  limb(P.m, at(0.5, 0), fork, tw, tw * 0.55);
  disc(P.m, fork[0], fork[1], tw * 0.28);
  limb(P.m, fork, at(0.12, 0.85), tw * 0.5, tw * 0.12);
  limb(P.m, at(0.27, 0.73), at(0.02, 0.64), tw * 0.25, tw * 0.07);
  limb(P.m, fork, at(0.9, 0.8), tw * 0.5, tw * 0.12);
  limb(P.m, at(0.74, 0.72), at(0.97, 0.98), tw * 0.25, tw * 0.07);
  limb(P.m, fork, at(0.55, 1), tw * 0.4, tw * 0.08);
  limb(P.m, at(0.52, 0.85), at(0.34, 0.97), tw * 0.2, tw * 0.06);
  poly(P.m, [at(0.28, 0), at(0.47, 0.13), at(0.47, 0)]);
  poly(P.m, [at(0.72, 0), at(0.53, 0.13), at(0.53, 0)]);
  if (p.crow) {
    const [cx, cy] = at(0.82, 0.77);
    crow(P, cx, cy, 3.2 * k, p.flip ? -1 : 1, t, p.phase);
  }
}

/** Corbeau perché, pattes en (fx, fy), tourné vers dir (±1), l'œil rouge qui luit. */
function crow(P, fx, fy, s, dir, t, phase) {
  oval(P.m, fx, fy - s * 1.1, s * 1.1, s * 0.75, dir * 0.35);
  disc(P.m, fx + dir * s * 0.9, fy - s * 1.9, s * 0.5);
  poly(P.m, [[fx + dir * s * 1.3, fy - s * 2.05], [fx + dir * s * 2.1, fy - s * 1.8], [fx + dir * s * 1.3, fy - s * 1.65]]);
  poly(P.m, [[fx - dir * s * 0.7, fy - s * 1.0], [fx - dir * s * 2.1, fy - s * 0.3], [fx - dir * s * 0.5, fy - s * 0.6]]);
  if (eyesOpen(t, phase)) disc(P.g, fx + dir * s * 1.0, fy - s * 2.0, Math.max(0.6, s * 0.14));
}

/** Butte arrondie posée sur l'horizon (îlot, tertre). */
function mound(path, x, base, w, h) {
  outline(path, [[x, base + 1], [x + w, base + 1, x + w / 2, base - h * 2]]);
}

// --- Plan lointain -----------------------------------------------------------------------
// Chaque dessinateur reçoit (P, x, base, w, h, p, k, t) : tracés, bord gauche et pied (px),
// taille (px), l'élément, l'échelle (px par unité monde) et le temps (s).

const FAR_DRAWERS = {
  building(P, x, base, w, h, p, k) {
    P.m.rect(x, base - h, w, h + 1);
    const ws = Math.max(1, 2.2 * k);
    for (const win of p.windows) P.g.rect(x + win.x * k, base - (p.h - win.y) * k, ws, ws * 1.3);
  },
  island(P, x, base, w, h, p, k) {
    mound(P.m, x, base, w, h);
    if (p.palm) palmTree(P.m, x + w * 0.62, base - h * 0.85, 13 * k, 22 * k, 0.4);
  },
  sail(P, x, base, w, h, p) {
    const at = frame(x, base, w, h, p);
    poly(P.m, [at(0, 0.18), at(1, 0.18), at(0.82, 0), at(0.18, 0)]);
    poly(P.m, [at(0.52, 1), at(0.95, 0.24), at(0.52, 0.24)]);
    poly(P.m, [at(0.46, 0.86), at(0.46, 0.24), at(0.1, 0.24)]);
  },
  conifer(P, x, base, w, h) {
    conifer(P, x, base, w, h);
  },
  peak(P, x, base, w, h, p) {
    const ax = x + w * p.apex;
    const ay = base - h;
    poly(P.m, [[x, base + 1], [ax, ay], [x + w, base + 1]]);
    if (p.snow) {
      // Calotte de neige, bord inférieur en dents de scie
      const f = 0.3;
      const lx = ax - (ax - x) * f;
      const rx = ax + (x + w - ax) * f;
      const sy = ay + h * f;
      const span = rx - lx;
      poly(P.a, [[ax, ay], [rx, sy], [rx - span * 0.22, sy - h * 0.03], [lx + span * 0.58, sy + h * 0.05],
        [lx + span * 0.32, sy - h * 0.02], [lx + span * 0.14, sy + h * 0.03], [lx, sy]]);
    }
  },
  ibex(P, x, base, w, h, p) {
    const at = frame(x, base, w, h, p);
    const [bx, by] = at(0.45, 0.55);
    oval(P.m, bx, by, w * 0.3, h * 0.17);
    for (const u of [0.22, 0.32, 0.58, 0.68]) limb(P.m, at(u, 0.5), at(u, 0), w * 0.07);
    limb(P.m, at(0.66, 0.6), at(0.82, 0.8), w * 0.12);
    const [hx, hy] = at(0.86, 0.8);
    oval(P.m, hx, hy, w * 0.09, h * 0.08);
    limb(P.m, at(0.84, 0.85), at(0.74, 1.05), w * 0.05, w * 0.02);
  },
  canopy(P, x, base, w, h) {
    P.m.rect(x + w * 0.46, base - h * 0.6, w * 0.08, h * 0.6 + 1);
    disc(P.m, x + w * 0.3, base - h * 0.62, w * 0.3);
    disc(P.m, x + w * 0.68, base - h * 0.66, w * 0.32);
    disc(P.m, x + w * 0.5, base - h * 0.84, w * 0.28);
  },
  emergent(P, x, base, w, h) {
    P.m.rect(x + w * 0.47, base - h * 0.88, w * 0.06, h * 0.88 + 1);
    oval(P.m, x + w * 0.5, base - h * 0.9, w * 0.5, h * 0.08);
    oval(P.m, x + w * 0.35, base - h * 0.96, w * 0.25, h * 0.06);
    oval(P.m, x + w * 0.66, base - h * 0.95, w * 0.22, h * 0.06);
  },
  deadtree: deadTree,
  chapel(P, x, base, w, h, p) {
    const at = frame(x, base, w, h, p);
    poly(P.m, [at(0, 0), at(0, 0.38), at(0.62, 0.38), at(0.62, 0)]);
    poly(P.m, [at(-0.03, 0.38), at(0.3, 0.56), at(0.64, 0.38)]);
    // Clocher au sommet effondré
    poly(P.m, [at(0.6, 0), at(0.6, 0.84), at(0.66, 0.93), at(0.71, 0.85), at(0.77, 1), at(0.83, 0.88),
      at(0.9, 0.91), at(0.9, 0)]);
    if (p.lit) {
      poly(P.g, [at(0.71, 0.55), at(0.71, 0.66), at(0.75, 0.7), at(0.79, 0.66), at(0.79, 0.55)]);
      poly(P.g, [at(0.22, 0.12), at(0.22, 0.24), at(0.28, 0.24), at(0.28, 0.12)]);
    }
  },
  mound(P, x, base, w, h) {
    mound(P.m, x, base, w, h);
  },
};

// --- Premier plan ------------------------------------------------------------------------

const NEAR_DRAWERS = {
  // Ville
  round(P, x, base, w, h) { // feuillage rond sur un tronc
    const trunk = w * 0.14;
    P.m.rect(x + w / 2 - trunk / 2, base - h * 0.45, trunk, h * 0.45);
    disc(P.m, x + w / 2, base - h * 0.6, w / 2);
    disc(P.m, x + w * 0.35, base - h * 0.78, w * 0.32);
  },
  conifer(P, x, base, w, h) {
    conifer(P, x, base, w, h);
  },
  house(P, x, base, w, h) { // corps + toit pointu + cheminée
    P.m.rect(x, base - h * 0.6, w, h * 0.6);
    poly(P.m, [[x - w * 0.06, base - h * 0.6], [x + w / 2, base - h], [x + w * 1.06, base - h * 0.6]]);
    P.m.rect(x + w * 0.7, base - h * 0.95, w * 0.1, h * 0.35);
  },
  church(P, x, base, w, h) { // clocher effilé et nef
    P.m.rect(x, base - h * 0.35, w, h * 0.35);
    P.m.rect(x + w * 0.35, base - h * 0.7, w * 0.3, h * 0.7);
    poly(P.m, [[x + w * 0.3, base - h * 0.7], [x + w / 2, base - h], [x + w * 0.7, base - h * 0.7]]);
  },
  grave(P, x, base, w, h, p) { // pierre tombale arrondie, parfois penchée
    const at = frame(x, base, w, h, p);
    outline(P.m, [at(0, 0), at(0, 0.6), [...at(0.5, 1), ...at(0, 1)], [...at(1, 0.6), ...at(1, 1)], at(1, 0)]);
  },

  // Plage
  palm(P, x, base, w, h, p) {
    palmTree(P.m, x + w / 2, base, w, h, p.lean);
  },
  hut(P, x, base, w, h, p) { // paillote sur pilotis
    P.m.rect(x + w * 0.14, base - h * 0.55, w * 0.72, h * 0.4);
    for (const u of [0.18, 0.5, 0.8]) P.m.rect(x + w * u, base - h * 0.2, Math.max(1, w * 0.04), h * 0.2);
    poly(P.m, [[x - w * 0.08, base - h * 0.5], [x + w * 0.5, base - h], [x + w * 1.08, base - h * 0.5]]);
    if (p.lit) P.g.rect(x + w * 0.42, base - h * 0.48, w * 0.16, h * 0.26);
  },
  bush(P, x, base, w, h, p, k, t) {
    oval(P.m, x + w * 0.3, base, w * 0.3, h * 0.75);
    oval(P.m, x + w * 0.62, base, w * 0.38, h);
    oval(P.m, x + w * 0.85, base, w * 0.2, h * 0.55);
    if (p.eyes && eyesOpen(t, p.phase)) eyes(P.g, x + w * 0.55, base - h * 0.55, 3.2 * k, Math.max(0.8, 1 * k));
  },
  rock(P, x, base, w, h) {
    poly(P.m, [[x, base + 1], [x + w * 0.12, base - h * 0.65], [x + w * 0.42, base - h], [x + w * 0.78, base - h * 0.82],
      [x + w, base + 1]]);
  },
  crab(P, x, base, w, h, p, k, t) {
    const at = frame(x, base, w, h, p);
    const [bx, by] = at(0.5, 0.4);
    oval(P.m, bx, by, w * 0.32, h * 0.35);
    const pinch = Math.sin(t * 3 + p.phase) * 0.08;
    for (const u of [0.08, 0.92]) {
      const [cx, cy] = at(u, 0.85 + pinch);
      disc(P.m, cx, cy, w * 0.13);
      limb(P.m, at(u < 0.5 ? 0.25 : 0.75, 0.45), [cx, cy], w * 0.07);
    }
    for (const u of [0.25, 0.4, 0.6, 0.75]) limb(P.m, at(u, 0.3), at(u + (u < 0.5 ? -0.12 : 0.12), 0), w * 0.05);
  },
  parasol(P, x, base, w, h, p) {
    const at = frame(x, base, w, h, p);
    limb(P.m, at(0.45, 0), at(0.55, 0.85), w * 0.05);
    outline(P.m, [at(0.02, 0.62), [...at(1.02, 0.78), ...at(0.45, 1.25)], at(0.8, 0.72), at(0.52, 0.7), at(0.26, 0.64)]);
  },

  // Forêt
  oak(P, x, base, w, h) {
    limb(P.m, [x + w * 0.5, base + 1], [x + w * 0.5, base - h * 0.5], w * 0.14, w * 0.09);
    limb(P.m, [x + w * 0.5, base - h * 0.42], [x + w * 0.26, base - h * 0.62], w * 0.06, w * 0.03);
    disc(P.m, x + w * 0.5, base - h * 0.66, w * 0.33);
    disc(P.m, x + w * 0.24, base - h * 0.58, w * 0.23);
    disc(P.m, x + w * 0.77, base - h * 0.6, w * 0.24);
    disc(P.m, x + w * 0.42, base - h * 0.82, w * 0.26);
    disc(P.m, x + w * 0.68, base - h * 0.8, w * 0.2);
  },
  deer(P, x, base, w, h, p, k, t) {
    const at = frame(x, base, w, h, p);
    const [bx, by] = at(0.42, 0.52);
    oval(P.m, bx, by, w * 0.3, h * 0.14);
    for (const [u1, u2] of [[0.2, 0.18], [0.3, 0.32], [0.56, 0.54], [0.65, 0.68]]) limb(P.m, at(u1, 0.48), at(u2, 0), w * 0.045, w * 0.03);
    limb(P.m, at(0.64, 0.56), at(0.8, 0.8), w * 0.11, w * 0.07);
    const [hx, hy] = at(0.86, 0.8);
    oval(P.m, hx, hy, w * 0.09, h * 0.055);
    poly(P.m, [at(0.8, 0.84), at(0.74, 0.92), at(0.82, 0.87)]);
    limb(P.m, at(0.82, 0.85), at(0.75, 1), w * 0.025);
    limb(P.m, at(0.78, 0.93), at(0.67, 0.97), w * 0.02);
    limb(P.m, at(0.86, 0.85), at(0.93, 1), w * 0.025);
    limb(P.m, at(0.9, 0.93), at(0.99, 0.95), w * 0.02);
    poly(P.m, [at(0.12, 0.6), at(0.07, 0.66), at(0.13, 0.52)]);
    if (eyesOpen(t, p.phase)) {
      const [ex, ey] = at(0.9, 0.81);
      disc(P.g, ex, ey, Math.max(0.7, 0.8 * k));
    }
  },
  fox(P, x, base, w, h, p, k, t) {
    const at = frame(x, base, w, h, p);
    const [bx, by] = at(0.45, 0.5);
    oval(P.m, bx, by, w * 0.27, h * 0.2);
    for (const u of [0.26, 0.34, 0.56, 0.64]) limb(P.m, at(u, 0.4), at(u, 0), w * 0.045);
    poly(P.m, [at(0.66, 0.68), at(0.98, 0.52), at(0.7, 0.4)]);
    poly(P.m, [at(0.7, 0.66), at(0.72, 0.98), at(0.8, 0.64)]);
    poly(P.m, [at(0.78, 0.63), at(0.84, 0.92), at(0.88, 0.6)]);
    // Grosse queue touffue qui balaie un peu
    const sweep = Math.sin(t * 2 + p.phase) * 0.12;
    const [tx, ty] = at(0.12, 0.42 + sweep);
    oval(P.m, tx, ty, w * 0.16, h * 0.14, (p.flip ? -1 : 1) * (0.5 + sweep));
    if (eyesOpen(t, p.phase)) {
      const [ex, ey] = at(0.8, 0.55);
      disc(P.g, ex, ey, Math.max(0.7, 0.8 * k));
    }
  },
  wolf(P, x, base, w, h, p) { // loup assis qui hurle à la lune
    const at = frame(x, base, w, h, p);
    poly(P.m, [at(0.12, 0), at(0.08, 0.2), at(0.2, 0.42), at(0.36, 0.58), at(0.46, 0.72), at(0.48, 0.86),
      at(0.5, 1), at(0.58, 0.9), at(0.76, 1.02), at(0.78, 0.95), at(0.64, 0.8), at(0.63, 0.6),
      at(0.67, 0.3), at(0.72, 0), at(0.6, 0), at(0.56, 0.25), at(0.44, 0.12), at(0.42, 0)]);
    oval(P.m, ...at(0.1, 0.06), w * 0.16, h * 0.05);
  },
  owl(P, x, base, w, h, p, k, t) { // souche morte et hibou perché dessus
    const at = frame(x, base, w, h, p);
    poly(P.m, [at(0.28, 0), at(0.36, 0.78), at(0.5, 0.81), at(0.64, 0.76), at(0.72, 0)]);
    limb(P.m, at(0.62, 0.48), at(1, 0.6), w * 0.12, w * 0.05);
    const s = 5.5 * k;
    const [cx, top] = at(0.5, 0.8);
    const cy = top - s * 0.95;
    oval(P.m, cx, cy, s * 0.62, s);
    poly(P.m, [[cx - s * 0.55, cy - s * 0.5], [cx - s * 0.62, cy - s * 1.25], [cx - s * 0.18, cy - s * 0.82]]);
    poly(P.m, [[cx + s * 0.55, cy - s * 0.5], [cx + s * 0.62, cy - s * 1.25], [cx + s * 0.18, cy - s * 0.82]]);
    if (eyesOpen(t, p.phase)) eyes(P.g, cx, cy - s * 0.45, s * 0.5, s * 0.17);
  },

  // Montagne
  chalet(P, x, base, w, h, p) {
    P.m.rect(x + w * 0.1, base - h * 0.55, w * 0.8, h * 0.55);
    poly(P.m, [[x - w * 0.06, base - h * 0.5], [x + w * 0.5, base - h * 0.95], [x + w * 1.06, base - h * 0.5]]);
    P.m.rect(x + w * 0.72, base - h, w * 0.08, h * 0.3);
    if (p.lit) {
      P.g.rect(x + w * 0.22, base - h * 0.4, w * 0.14, h * 0.16);
      P.g.rect(x + w * 0.62, base - h * 0.4, w * 0.14, h * 0.16);
    }
  },

  // Jungle
  bigleaf(P, x, base, w, h, p, k, t) { // bananier / oreille d'éléphant
    const at = frame(x, base, w, h, p);
    for (const [u, v, rot] of [[0.12, 0.45, -1.0], [0.3, 0.72, -0.55], [0.52, 0.88, 0.05], [0.72, 0.74, 0.5], [0.9, 0.46, 1.0]]) {
      const [lx, ly] = at(u, v);
      limb(P.m, at(0.5, 0), at(u, v * 0.6), Math.max(1, w * 0.035));
      oval(P.m, lx, ly, w * 0.12, h * 0.26, p.flip ? -rot : rot);
    }
    if (p.eyes && eyesOpen(t, p.phase)) eyes(P.g, ...at(0.5, 0.22), 3.6 * k, Math.max(0.8, 1.1 * k));
  },
  fern(P, x, base, w, h) {
    const cx = x + w / 2;
    for (const a of [-1.3, -0.85, -0.4, 0, 0.4, 0.85, 1.3]) {
      const reach = 1 - Math.abs(a) * 0.22;
      limb(P.m, [cx, base + 1], [cx + Math.sin(a) * w * 0.5, base - Math.cos(a) * h * reach], w * 0.1, w * 0.02);
    }
  },
  kapok(P, x, base, w, h, p, k, t) { // fromager : contreforts, cime plate, lianes, parfois un singe
    const at = frame(x, base, w, h, p);
    poly(P.m, [at(0.4, 0), at(0.43, 0.76), at(0.57, 0.76), at(0.6, 0)]);
    poly(P.m, [at(0.2, 0), at(0.42, 0.28), at(0.44, 0)]);
    poly(P.m, [at(0.8, 0), at(0.58, 0.28), at(0.56, 0)]);
    oval(P.m, ...at(0.5, 0.84), w * 0.5, h * 0.09);
    oval(P.m, ...at(0.22, 0.8), w * 0.26, h * 0.07);
    oval(P.m, ...at(0.78, 0.82), w * 0.26, h * 0.08);
    oval(P.m, ...at(0.5, 0.93), w * 0.3, h * 0.07);
    const lw = Math.max(1, 0.8 * k);
    limb(P.m, at(0.12, 0.76), at(0.14, 0.34), lw);
    limb(P.m, at(0.3, 0.76), at(0.29, 0.5), lw);
    const sway = Math.sin(t * 1.3 + p.phase) * 3 * k;
    const anchor = at(0.84, 0.78);
    const [ex, ey] = at(0.84, 0.42);
    limb(P.m, anchor, [ex + sway, ey], lw);
    if (p.monkey) monkey(P, ex + sway, ey, 4.5 * k, sway);
  },
  toucan(P, x, base, w, h, p, k) { // toucan sur une souche, le bec coloré
    const at = frame(x, base, w, h, p);
    poly(P.m, [at(0.22, 0), at(0.3, 0.55), at(0.7, 0.58), at(0.78, 0)]);
    const dir = p.flip ? -1 : 1;
    const s = 3.6 * k;
    const [cx, top] = at(0.5, 0.57);
    oval(P.m, cx, top - s * 1.3, s * 0.72, s * 1.3, dir * 0.2);
    disc(P.m, cx + dir * s * 0.25, top - s * 2.6, s * 0.6);
    poly(P.m, [[cx - dir * s * 0.4, top - s * 0.5], [cx - dir * s * 0.5, top + s * 1.1], [cx - dir * s * 0.05, top + s * 1.1]]);
    outline(P.a, [[cx + dir * s * 0.6, top - s * 2.95], [cx + dir * s * 2.7, top - s * 2.45, cx + dir * s * 1.8, top - s * 3.3],
      [cx + dir * s * 0.6, top - s * 2.25, cx + dir * s * 1.6, top - s * 2.35]]);
  },

  // Cimetière
  cross(P, x, base, w, h, p) {
    const at = frame(x, base, w, h, p);
    poly(P.m, [at(0.38, 0), at(0.38, 1), at(0.62, 1), at(0.62, 0)]);
    poly(P.m, [at(0, 0.62), at(0, 0.78), at(1, 0.78), at(1, 0.62)]);
    oval(P.m, x + w / 2, base + 1, w * 0.7, h * 0.07);
  },
  obelisk(P, x, base, w, h) {
    P.m.rect(x, base - h * 0.12, w, h * 0.12 + 1);
    poly(P.m, [[x + w * 0.24, base - h * 0.12], [x + w * 0.32, base - h * 0.86], [x + w * 0.68, base - h * 0.86], [x + w * 0.76, base - h * 0.12]]);
    poly(P.m, [[x + w * 0.3, base - h * 0.86], [x + w * 0.5, base - h], [x + w * 0.7, base - h * 0.86]]);
  },
  fence(P, x, base, w, h, p, k) { // grille en fer forgé aux barreaux en pointe de lance
    const rail = Math.max(1, k);
    const bar = Math.max(1, 1.1 * k);
    const step = 5 * k;
    P.m.rect(x, base - h * 0.3, w, rail);
    P.m.rect(x, base - h * 0.8, w, rail);
    for (let bx = x + step / 2; bx < x + w; bx += step) {
      P.m.rect(bx - bar / 2, base - h * 0.9, bar, h * 0.9 + 1);
      poly(P.m, [[bx - bar * 1.3, base - h * 0.88], [bx, base - h], [bx + bar * 1.3, base - h * 0.88]]);
    }
    P.m.rect(x - bar, base - h * 1.05, bar * 2.4, h * 1.05 + 1);
    P.m.rect(x + w - bar, base - h * 1.05, bar * 2.4, h * 1.05 + 1);
  },
  deadtree: deadTree,
  crypt(P, x, base, w, h, p, k, t) { // caveau à fronton, avec des yeux qui guettent dans l'entrée
    P.m.rect(x, base - h * 0.06, w, h * 0.06 + 1);
    P.m.rect(x + w * 0.08, base - h * 0.6, w * 0.84, h * 0.6);
    P.m.rect(x - w * 0.03, base - h * 0.64, w * 1.06, h * 0.05);
    poly(P.m, [[x - w * 0.02, base - h * 0.63], [x + w * 0.5, base - h * 0.84], [x + w * 1.02, base - h * 0.63]]);
    limb(P.m, [x + w * 0.5, base - h * 0.82], [x + w * 0.5, base - h], w * 0.05);
    limb(P.m, [x + w * 0.43, base - h * 0.94], [x + w * 0.57, base - h * 0.94], w * 0.04);
    if (p.eyes) {
      if (eyesOpen(t, p.phase)) eyes(P.g, x + w * 0.5, base - h * 0.3, 3 * k, Math.max(0.8, 0.9 * k));
    } else {
      P.g.rect(x + w * 0.495, base - h * 0.46, Math.max(1, 0.5 * k), h * 0.4);
    }
  },
};

/** Singe suspendu d'une main au bout d'une liane (hx, hy). */
function monkey(P, hx, hy, s, sway) {
  const bx = hx + sway * 0.3;
  const by = hy + s * 2.3;
  limb(P.m, [hx, hy], [bx + s * 0.3, by - s * 0.6], s * 0.32);
  oval(P.m, bx, by, s * 0.68, s * 0.95);
  disc(P.m, bx + s * 0.45, by - s * 1.15, s * 0.55);
  disc(P.m, bx + s * 0.02, by - s * 1.3, s * 0.2);
  limb(P.m, [bx - s * 0.25, by + s * 0.7], [bx - s * 0.55, by + s * 1.7], s * 0.3);
  limb(P.m, [bx + s * 0.3, by + s * 0.7], [bx + s * 0.4, by + s * 1.7], s * 0.3);
  limb(P.m, [bx - s * 0.4, by + s * 0.3], [bx - s * 1.4, by + s * 0.9], s * 0.18);
  limb(P.m, [bx - s * 1.4, by + s * 0.9], [bx - s * 1.5, by + s * 0.2], s * 0.18);
}

// --- Oiseaux et chauves-souris -----------------------------------------------------------

function bird(path, cx, cy, s, t, phase) {
  const flap = Math.sin(t * 5 + phase) * s * 0.35;
  const lift = flap * 0.4;
  outline(path, [
    [cx - s, cy - flap, cx - s * 0.45, cy - s * 0.12 - lift],
    [cx, cy, cx - s * 0.45, cy - s * 0.4 - lift],
    [cx + s, cy - flap, cx + s * 0.45, cy - s * 0.4 - lift],
    [cx, cy + s * 0.22, cx + s * 0.45, cy - s * 0.12 - lift],
  ]);
}

function bat(path, cx, cy, s, t, phase) {
  const flap = Math.sin(t * 14 + phase) * s * 0.5;
  oval(path, cx, cy, s * 0.22, s * 0.35);
  disc(path, cx, cy - s * 0.38, s * 0.17);
  for (const d of [-1, 1]) {
    poly(path, [[cx + d * s * 0.05, cy - s * 0.48], [cx + d * s * 0.18, cy - s * 0.68], [cx + d * s * 0.17, cy - s * 0.4]]);
    poly(path, [[cx, cy - s * 0.2], [cx + d * s * 0.5, cy - s * 0.45 - flap * 0.5], [cx + d * s * 1.1, cy - s * 0.3 - flap],
      [cx + d * s * 0.85, cy + s * 0.05 - flap * 0.6], [cx + d * s * 0.65, cy - s * 0.05 - flap * 0.4],
      [cx + d * s * 0.45, cy + s * 0.15 - flap * 0.3], [cx + d * s * 0.25, cy + s * 0.05], [cx, cy + s * 0.1]]);
  }
}

// --- Peintre -----------------------------------------------------------------------------

export function createSceneryPainter(ctx, canvas) {
  const ground = createGroundPainter(ctx);
  const mod = (a, n) => ((a % n) + n) % n;

  function drawSky(pal, horizonPx, k) {
    const px = canvas.width;
    const py = canvas.height;
    // Le dégradé est ancré sur l'horizon : quand on lève l'objectif, on voit le ciel s'assombrir
    const sky = ctx.createLinearGradient(0, horizonPx - 640 * k, 0, horizonPx);
    sky.addColorStop(0, pal.skyTop);
    sky.addColorStop(0.55, pal.skyMid);
    sky.addColorStop(0.88, pal.skyGlow);
    sky.addColorStop(1, pal.skyHorizon);
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, px, Math.max(0, Math.min(py, horizonPx)));

    if (horizonPx < py) {
      const groundGrad = ctx.createLinearGradient(0, horizonPx, 0, horizonPx + 640 * k);
      groundGrad.addColorStop(0, pal.groundTop);
      groundGrad.addColorStop(0.25, pal.groundBottom);
      groundGrad.addColorStop(1, pal.ground?.deep ?? pal.groundBottom);
      ctx.fillStyle = groundGrad;
      ctx.fillRect(0, Math.max(0, horizonPx), px, py);
    }
  }

  /** Vue (unités) d'un point du ciel lointain : suit la parallaxe horizontale du plan lointain. */
  // Période du plan lointain (worldSize × parallax), fixée à chaque paint : tout ce qui est dans son ciel boucle dessus
  let farPeriod = 0;
  const farView = (x, y, camera) => [wrapX(x - camera.x * FAR_PARALLAX, farPeriod) + view.width / 2, y - camera.y + VIEW_SIZE / 2];

  function drawStars(stars, camera, horizonPx, k, pal, t) {
    if (!stars.length) return;
    ctx.fillStyle = pal.star;
    for (const s of stars) {
      const [vx, vy] = farView(s.x, s.y, camera);
      if (vx < -2 || vx > view.width + 2 || vy < -2 || vy * k > horizonPx - 4 * k) continue;
      ctx.globalAlpha = 0.35 + 0.65 * Math.abs(Math.sin(t * 0.7 + s.phase));
      const size = Math.max(1, s.r * k);
      ctx.fillRect(vx * k, vy * k, size, size);
    }
    ctx.globalAlpha = 1;
  }

  function drawBody(body, camera, k, pal) {
    const [vx, vy] = farView(body.x, body.y, camera);
    const reach = body.kind === 'sun' ? 4.5 : 3;
    const m = body.r * reach;
    if (vx < -m || vx > view.width + m || vy < -m || vy > VIEW_SIZE + m) return;
    const x = vx * k;
    const y = vy * k;
    const r = body.r * k;
    const halo = ctx.createRadialGradient(x, y, r * 0.8, x, y, r * reach);
    halo.addColorStop(0, pal.bodyHalo);
    halo.addColorStop(1, pal.bodyHaloEdge);
    ctx.fillStyle = halo;
    ctx.fillRect(x - r * reach, y - r * reach, r * reach * 2, r * reach * 2);
    ctx.fillStyle = pal.body;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fill();
    if (body.kind === 'crescent') {
      // Croissant : on « mord » la lune avec un disque de la couleur du ciel
      ctx.fillStyle = pal.skyTop;
      ctx.beginPath();
      ctx.arc(x + r * 0.45, y - r * 0.2, r * 0.85, 0, TAU);
      ctx.fill();
    } else if (body.kind === 'full') {
      // Pleine lune et ses mers sombres
      const seas = new Path2D();
      disc(seas, x - r * 0.3, y - r * 0.2, r * 0.28);
      disc(seas, x + r * 0.25, y + r * 0.25, r * 0.22);
      disc(seas, x + r * 0.35, y - r * 0.35, r * 0.12);
      disc(seas, x - r * 0.15, y + r * 0.45, r * 0.1);
      ctx.fillStyle = pal.bodyShade;
      ctx.fill(seas);
    }
  }

  /** Mer entre l'horizon lointain (où voguent îlots et voiliers) et la plage, avec le reflet du soleil. */
  function drawSea(sea, body, camera, horizonPx, k, pal, t) {
    if (!sea || !pal.sea) return;
    const top = horizonPx - sea.depth * k;
    const grad = ctx.createLinearGradient(0, top, 0, horizonPx);
    grad.addColorStop(0, pal.sea.top);
    grad.addColorStop(1, pal.sea.bottom);
    ctx.fillStyle = grad;
    ctx.fillRect(0, top, canvas.width, sea.depth * k + 1);
    if (!body) return;
    const bx = farView(body.x, body.y, camera)[0] * k;
    ctx.fillStyle = pal.sea.glint;
    const rows = 7;
    for (let i = 0; i < rows; i++) {
      const y = top + ((i + 0.4) * sea.depth * k) / rows;
      const half = body.r * k * (0.95 - i * 0.07) * (0.7 + 0.3 * Math.sin(t * 2 + i * 1.3));
      const drift = Math.sin(t * 1.1 + i) * 2 * k;
      ctx.fillRect(bx - half + drift, y, half * 2, Math.max(1, 0.7 * k));
    }
  }

  // `center` = abscisse du plan au centre du viseur, `period` = largeur du plan : il boucle dessus, un élément
  // qui passe la couture réapparaît à l'autre bout (repéré par son bord gauche, il est dessiné en entier).
  function drawLayer(items, drawers, center, period, horizonPx, lift, k, colors, t) {
    for (const p of items) {
      const vx = wrapX(p.x - center, period) + view.width / 2;
      if (vx + p.w < -40 || vx > view.width + 40) continue;
      const draw = drawers[p.kind];
      if (!draw) continue;
      const P = { m: new Path2D(), a: new Path2D(), g: new Path2D() };
      draw(P, vx * k, horizonPx + 1 - (lift + (p.lift || 0)) * k, p.w * k, p.h * k, p, k, t);
      ctx.fillStyle = colors.main;
      ctx.fill(P.m);
      ctx.fillStyle = colors.accent;
      ctx.fill(P.a);
      ctx.fillStyle = colors.glow;
      ctx.fill(P.g);
    }
  }

  // Voie lactée : un halo très diffus en fuseau (large au milieu, fondu aux deux bouts) et légèrement courbe, fait de
  // taches elliptiques qui se recouvrent le long de son axe, à trois largeurs. Les étoiles serrées qui la dessinent
  // sont dans scenery.stars.
  function drawMilkyWay(mw, camera, horizonPx, k, t) {
    if (!mw) return;
    const centerX = farView(mw.x0, 0, camera)[0] * k;
    if (centerX < -(mw.width * 3 + 80) * k || centerX > canvas.width + (mw.width * 3 + 80) * k) return;
    const shimmer = 0.92 + 0.08 * Math.sin(t * 0.3);
    const N = 11;
    const step = (mw.ry * 2) / N;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, canvas.width, Math.max(0, horizonPx));
    ctx.clip();
    for (const [spread, alpha] of [[2.4, 0.045], [1.5, 0.06], [0.8, 0.07]]) {
      for (let i = 0; i < N; i++) {
        const u = -1 + (2 * (i + 0.5)) / N;
        const y = mw.yc + u * mw.ry;
        const taper = milkyWayTaper(u);
        const [vx, vy] = farView(milkyWayAxis(mw, y), y, camera);
        const rx = mw.width * spread * (0.3 + 0.7 * taper) * k;
        const ry = step * 1.5 * k;
        if (vy * k + ry < 0 || vy * k - ry > horizonPx) continue;
        ctx.save();
        ctx.translate(vx * k, vy * k);
        ctx.scale(rx, ry);
        const a = alpha * shimmer * (0.35 + 0.65 * taper);
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
        g.addColorStop(0, `rgba(205, 215, 255, ${a})`);
        g.addColorStop(0.5, `rgba(200, 212, 255, ${a * 0.45})`);
        g.addColorStop(1, 'rgba(190, 205, 255, 0)');
        ctx.fillStyle = g;
        ctx.fillRect(-1, -1, 2, 2);
        ctx.restore();
      }
    }
    ctx.restore();
  }

  // Avion lointain : une silhouette minuscule, sa traînée, un feu rouge fixe et un flash blanc qui clignote
  function drawPlane(plane, camera, horizonPx, k, pal, t) {
    if (!plane) return;
    const dir = Math.sign(plane.speed) || 1;
    const half = plane.span / 2;
    const x = ((((plane.x0 + t * plane.speed + half) % plane.span) + plane.span) % plane.span) - half; // boucle sur la largeur du ciel
    const [vx, vy] = farView(x, plane.y, camera);
    const s = plane.s * k;
    if (vx < -s * 12 || vx > view.width + s * 12 || vy * k > horizonPx - 6 * k) return;
    const px = vx * k;
    const py = vy * k;
    const trail = ctx.createLinearGradient(px, 0, px - dir * s * 14, 0);
    trail.addColorStop(0, pal.star);
    trail.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = trail;
    ctx.fillRect(Math.min(px, px - dir * s * 14), py - s * 0.12, s * 14, s * 0.24);
    ctx.globalAlpha = 1;
    const body = new Path2D();
    oval(body, px, py, s * 1.6, s * 0.28); // fuselage
    poly(body, [[px - dir * s * 0.2, py], [px + dir * s * 0.5, py + s * 0.05], [px - dir * s * 0.7, py + s * 0.85]]); // aile
    poly(body, [[px - dir * s * 1.3, py - s * 0.1], [px - dir * s * 1.7, py - s * 0.75], [px - dir * s * 1.15, py - s * 0.1]]); // dérive
    ctx.fillStyle = pal.flyer;
    ctx.fill(body);
    ctx.fillStyle = '#ff4a3a'; // feu de position, fixe
    ctx.beginPath();
    ctx.arc(px - dir * s * 0.7, py + s * 0.85, Math.max(0.8, s * 0.16), 0, Math.PI * 2);
    ctx.fill();
    const flash = (t + plane.phase) % 1.6 < 0.12; // flash blanc, deux battements courts
    if (flash) {
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(px - dir * s * 1.7, py - s * 0.75, Math.max(1, s * 0.22), 0, Math.PI * 2);
      ctx.fill();
    }
  }

  function drawFlyers(flyers, camera, k, pal, t) {
    if (!flyers.length) return;
    const path = new Path2D();
    for (const f of flyers) {
      // Chacun tourne un peu autour de sa position ; les chauves-souris, plus vite et en zigzag
      const batty = f.kind === 'bat';
      const x = f.x + Math.sin(t * (batty ? 0.8 : 0.25) + f.phase) * (batty ? 26 : 34);
      const y = f.y + Math.sin(t * (batty ? 2.1 : 0.6) + f.phase * 1.7) * (batty ? 9 : 5);
      const [vx, vy] = farView(x, y, camera);
      if (vx < -f.s * 2 || vx > view.width + f.s * 2 || vy < -f.s * 2 || vy > VIEW_SIZE + f.s * 2) continue;
      (batty ? bat : bird)(path, vx * k, vy * k, f.s * k, t, f.phase);
    }
    ctx.fillStyle = pal.flyer;
    ctx.fill(path);
  }

  function drawHorizonLine(horizonPx, k, pal) {
    // Filet lumineux sur la ligne d'horizon (dernier rayon du soleil, écume, reflet de lune…)
    ctx.fillStyle = pal.horizonLine;
    ctx.fillRect(0, horizonPx - Math.max(1, k), canvas.width, Math.max(1, k * 1.5));
  }

  /** Brume au ras du sol, et au cimetière des nappes qui dérivent lentement. */
  function drawFog(camera, horizonPx, k, pal, t, worldSize) {
    if (!pal.fog) return;
    const top = horizonPx - 40 * k;
    const bottom = horizonPx + 30 * k;
    const band = ctx.createLinearGradient(0, top, 0, bottom);
    band.addColorStop(0, pal.fogEdge);
    band.addColorStop(0.6, pal.fog);
    band.addColorStop(1, pal.fogEdge);
    ctx.fillStyle = band;
    ctx.fillRect(0, top, canvas.width, bottom - top);
    if (!pal.fogWisps) return;
    // Les volutes bouclent sur worldSize / n (n entier) : au passage de la couture, elles se retrouvent exactement
    // là où elles étaient
    const n = Math.max(1, Math.round(worldSize / 640));
    const period = worldSize / n;
    for (let i = 0; i < 5; i++) {
      const vx = mod(i * 137 + t * (5 + (i % 3) * 3) - camera.x, period) - 160;
      const x = vx * k;
      const y = horizonPx + ((i % 3) - 1) * 9 * k;
      const r = (60 + (i % 2) * 30) * k;
      ctx.save();
      ctx.translate(x, y);
      ctx.scale(1, 0.16);
      const wisp = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
      wisp.addColorStop(0, pal.fog);
      wisp.addColorStop(1, pal.fogEdge);
      ctx.fillStyle = wisp;
      ctx.fillRect(-r, -r, r * 2, r * 2);
      ctx.restore();
    }
  }

  /**
   * Dessine le décor complet derrière les fantômes.
   * @param {import('../core/scenery.js').Scenery|null} scenery
   * @param {object} pal  palette du décor (config/theme.js#sceneryPalettes)
   */
  function paint(scenery, pal, camera, horizonPx, k, t) {
    drawSky(pal, horizonPx, k);
    farPeriod = (scenery?.worldSize ?? 0) * FAR_PARALLAX;
    if (!scenery) {
      drawHorizonLine(horizonPx, k, pal);
      return;
    }
    const seaDepth = scenery.sea && pal.sea ? scenery.sea.depth : 0;
    drawMilkyWay(scenery.milkyWay, camera, horizonPx - seaDepth * k, k, t);
    drawStars(scenery.stars, camera, horizonPx - seaDepth * k, k, pal, t);
    drawBody(scenery.body, camera, k, pal);
    drawPlane(scenery.plane, camera, horizonPx - seaDepth * k, k, pal, t);
    drawSea(scenery.sea, scenery.body, camera, horizonPx, k, pal, t);
    drawLayer(scenery.far, FAR_DRAWERS, camera.x * FAR_PARALLAX, farPeriod, horizonPx, seaDepth, k,
      { main: pal.far, accent: pal.farAccent, glow: pal.farGlow }, t);
    drawFlyers(scenery.flyers, camera, k, pal, t);
    drawHorizonLine(horizonPx, k, pal);
    drawLayer(scenery.near, NEAR_DRAWERS, camera.x, scenery.worldSize, horizonPx, 0, k,
      { main: pal.near, accent: pal.nearAccent, glow: pal.nearGlow }, t);
    ground.paint(scenery.ground, scenery.body, camera, k, pal, t);
    drawFog(camera, horizonPx, k, pal, t, scenery.worldSize);
  }

  return { paint };
}
